import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureFlagsManager } from '../../../../../../utils/core/feature-flags-manager';
import { Cesium3dTilesPointCloudRenderer } from '../../../../../../viewer/geodata/point-clouds/cesium-3d-tiles-point-cloud-renderer/cesium-3d-tiles-point-cloud-renderer';
import { Cesium3dTilesPointsMaterial } from '../../../../../../viewer/geodata/point-clouds/cesium-3d-tiles-point-cloud-renderer/cesium-3d-tiles-points-material';
import { EdlPass } from '../../../../../../viewer/geodata/point-clouds/cesium-3d-tiles-point-cloud-renderer/edl-pass';
import { CesiumPointsMaterial } from '../../../../../../viewer/geodata/point-clouds/cesium-point-cloud/points/material/cesium-point-material';
import { CopcPointCloud } from '../../../../../../viewer/geodata/point-clouds/copc-point-cloud/copc-point-cloud';
import { PointCloudHandle } from '../../../../../../viewer/geodata/point-clouds/point-cloud-handle';
import { PointSizeInterpolationType } from '../../../../../../viewer/geodata/point-clouds/types';
import { useUnitSymbols } from '../../../../../hooks/use-unit-symbols';
import { useOptionalUserState } from '../../../../../state/user-state/user-state';
import { LabelledContainer } from '../../../../labelled-container/labelled-container';
import { Select } from '../../../../select/select';
import { Slider } from '../../../../slider/slider';
import { Stack } from '../../../../stack/stack';
import { Switch } from '../../../../switch/switch';
import { CopcSettings } from './copc-settings';
import { PointCloudClassifications } from './point-cloud-classifications';

type SliderMapping = {
  [key in PointSizeInterpolationType]: { min: number; max: number };
};

type ClassificationOption = 'Yes' | 'No';

const sliderValues: SliderMapping = {
  ['Dynamic']: { min: 0.1, max: 1.0 },
  ['Fixed']: { min: 1.0, max: 5.0 },
};

const PointCloudSettings = () => {
  const { t } = useTranslation();
  const unitSymbols = useUnitSymbols();

  const activePointSizeInterpolationSettings =
    PointCloudHandle.instance.pointSizeInterpolationSettings;

  const [pointCloudPointSizeInterpolationType, setPointCloudPointSizeInterpolationType] =
    React.useState(activePointSizeInterpolationSettings.pointSizeInterpolationType);
  const [pointCloudPointSize, setPointCloudPointSize] = React.useState(
    activePointSizeInterpolationSettings.size,
  );
  const [showPointCloudClassifications, setShowPointCloudClassifications] = React.useState(
    CesiumPointsMaterial.instance.displayClasses,
  );

  // Extra settings (SK-7002-extra-point-cloud-settings)
  const mat = Cesium3dTilesPointsMaterial.instance;
  const [edlEnabled, setEdlEnabled] = React.useState(EdlPass.current?.enabled ?? false);
  const [edlStrength, setEdlStrength] = React.useState(EdlPass.current?.edlStrength ?? 1.0);
  const [edlRadius, setEdlRadius] = React.useState(EdlPass.current?.edlRadius ?? 1.5);
  const [pointSizeScale, setPointSizeScale] = React.useState(mat.pointSizeScale);
  const [maximumAttenuation, setMaximumAttenuation] = React.useState(mat.maximumAttenuation);
  const [baseResolution, setBaseResolution] = React.useState(
    Cesium3dTilesPointCloudRenderer.current?._baseResolution ?? 0.05,
  );

  const showExtraSettings = FeatureFlagsManager.instance.isEnabled(
    'SK-7002-extra-point-cloud-settings',
  );
  // Shared views may have no user, who then isn't an organization admin
  const showCopcSettings =
    (useOptionalUserState()?.isOrganizationAdmin ?? false) && CopcPointCloud.current !== undefined;

  const onSetShowPointCloudClassifications = (value: ClassificationOption) => {
    const show = value === 'Yes';
    CesiumPointsMaterial.instance.displayClasses = show;
    mat.displayClasses = show;
    setShowPointCloudClassifications(show);
  };

  const setPointSizeInterpolationType = (value: PointSizeInterpolationType) => {
    // Calculate current slider percent to map to same slider position for the other range.
    const percent =
      (pointCloudPointSize - sliderValues[pointCloudPointSizeInterpolationType].min) /
      (sliderValues[pointCloudPointSizeInterpolationType].max -
        sliderValues[pointCloudPointSizeInterpolationType].min);

    setPointCloudPointSize(
      sliderValues[value].min + (sliderValues[value].max - sliderValues[value].min) * percent,
    );

    setPointCloudPointSizeInterpolationType(value);
  };

  useEffect(() => {
    PointCloudHandle.instance.setPointSizeInterpolationSettings({
      pointSizeInterpolationType: pointCloudPointSizeInterpolationType,
      size: pointCloudPointSize,
    });
  }, [pointCloudPointSize, pointCloudPointSizeInterpolationType]);

  const onEdlEnabled = (e: React.MouseEvent<HTMLInputElement>) => {
    const enabled = (e.target as HTMLInputElement).checked;
    if (EdlPass.current) {
      EdlPass.current.enabled = enabled;
    }
    setEdlEnabled(enabled);
  };

  const onEdlStrength = (v: number) => {
    if (EdlPass.current) {
      EdlPass.current.edlStrength = v;
    }
    setEdlStrength(v);
  };

  const onEdlRadius = (v: number) => {
    if (EdlPass.current) {
      EdlPass.current.edlRadius = v;
    }
    setEdlRadius(v);
  };

  const onPointSizeScale = (v: number) => {
    mat.pointSizeScale = v;
    setPointSizeScale(v);
  };

  const onMaximumAttenuation = (v: number) => {
    mat.maximumAttenuation = v;
    setMaximumAttenuation(v);
  };

  const onBaseResolution = (v: number) => {
    if (Cesium3dTilesPointCloudRenderer.current) {
      Cesium3dTilesPointCloudRenderer.current._baseResolution = v;
    }
    setBaseResolution(v);
  };

  return (
    <Stack direction="row" spacing={2}>
      <Stack direction="column" flex={1} spacing={1}>
        <LabelledContainer
          noWrap={true}
          text={t('pointcloud.settings.showClassifications.title', { ns: 'skyviewToolbar' })}
        >
          {(formElementId) => (
            <Select
              id={formElementId()}
              options={[
                { id: 'Yes', name: t('yes', { ns: 'common' }) },
                { id: 'No', name: t('no', { ns: 'common' }) },
              ]}
              value={showPointCloudClassifications ? 'Yes' : 'No'}
              onChange={(e) =>
                onSetShowPointCloudClassifications(e.target.value as ClassificationOption)
              }
            />
          )}
        </LabelledContainer>
        <LabelledContainer
          text={t('pointcloud.settings.pointCloudSettings.title', { ns: 'skyviewToolbar' })}
        >
          {(formElementId) => (
            <Select
              id={formElementId()}
              options={[
                {
                  id: 'Dynamic',
                  name: t('pointcloud.settings.pointCloudSettings.values.dynamic', {
                    ns: 'skyviewToolbar',
                  }),
                },
                {
                  id: 'Fixed',
                  name: t('pointcloud.settings.pointCloudSettings.values.fixed', {
                    ns: 'skyviewToolbar',
                  }),
                },
              ]}
              value={pointCloudPointSizeInterpolationType}
              onChange={(e) =>
                setPointSizeInterpolationType(e.target.value as PointSizeInterpolationType)
              }
            />
          )}
        </LabelledContainer>
        <LabelledContainer
          noWrap={true}
          text={t('pointcloud.settings.pointCloudSize.title', {
            ns: 'skyviewToolbar',
            value: pointCloudPointSize.toFixed(2),
          })}
        >
          <Slider
            max={sliderValues[pointCloudPointSizeInterpolationType].max}
            min={sliderValues[pointCloudPointSizeInterpolationType].min}
            step={0.02}
            value={pointCloudPointSize}
            onChange={setPointCloudPointSize}
          />
        </LabelledContainer>

        {showCopcSettings && <CopcSettings />}

        {showExtraSettings && (
          <>
            <LabelledContainer
              noWrap={true}
              text={t('pointcloud.settings.edl.enabled.title', { ns: 'skyviewToolbar' })}
            >
              <Switch checked={edlEnabled} onChange={onEdlEnabled} />
            </LabelledContainer>
            {edlEnabled && (
              <>
                <LabelledContainer
                  noWrap={true}
                  text={t('pointcloud.settings.edl.strength.title', {
                    ns: 'skyviewToolbar',
                    value: edlStrength.toFixed(1),
                  })}
                >
                  <Slider
                    max={5.0}
                    min={0.0}
                    step={0.1}
                    value={edlStrength}
                    onChange={onEdlStrength}
                  />
                </LabelledContainer>
                <LabelledContainer
                  noWrap={true}
                  text={t('pointcloud.settings.edl.radius.title', {
                    ns: 'skyviewToolbar',
                    value: edlRadius.toFixed(1),
                  })}
                >
                  <Slider max={5.0} min={0.5} step={0.1} value={edlRadius} onChange={onEdlRadius} />
                </LabelledContainer>
              </>
            )}
            <LabelledContainer
              noWrap={true}
              text={t('pointcloud.settings.rendering.pointSizeScale.title', {
                ns: 'skyviewToolbar',
                value: pointSizeScale.toFixed(1),
              })}
            >
              <Slider
                max={5.0}
                min={0.1}
                step={0.1}
                value={pointSizeScale}
                onChange={onPointSizeScale}
              />
            </LabelledContainer>
            <LabelledContainer
              noWrap={true}
              text={t('pointcloud.settings.rendering.maximumAttenuation.title', {
                ns: 'skyviewToolbar',
                value: Math.round(maximumAttenuation),
              })}
            >
              <Slider
                max={100}
                min={1}
                step={1}
                value={maximumAttenuation}
                onChange={onMaximumAttenuation}
              />
            </LabelledContainer>
            {Cesium3dTilesPointCloudRenderer.current && (
              <LabelledContainer
                noWrap={true}
                text={t('pointcloud.settings.rendering.baseResolution.title', {
                  ns: 'skyviewToolbar',
                  value: baseResolution.toFixed(3),
                  unit: unitSymbols.length,
                })}
              >
                <Slider
                  max={0.5}
                  min={0.005}
                  step={0.005}
                  value={baseResolution}
                  onChange={onBaseResolution}
                />
              </LabelledContainer>
            )}
          </>
        )}
      </Stack>

      {showPointCloudClassifications && <PointCloudClassifications />}
    </Stack>
  );
};

export { PointCloudSettings };
